import React, { FC, useMemo } from 'react';
import { hubPageBg } from './accountHubStyles';
import { useParams } from 'react-router';
import { Box, Divider, Link, Stack, Typography } from '@mui/material';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/rootReducerType';
import type { Certification } from 'src/types/Certification';
import { RENTER_PATHS } from 'src/routes/renterPaths';
import PageHeader from 'src/components/NewRenterInterface/PageHeader/PageHeader';

const PropertyListPage: FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const targetUserId = userId ? Number(userId) : null;

  const user = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user
  );
  const certifications = useSelector(
    (state: RootState) => state.newRenterInterface.certifications
  ) as Certification[];

  // Derive the effective user ID — the person whose properties we are showing.
  // If targetUserId is present and different from the logged-in user, we are
  // viewing another member's hub; otherwise show the logged-in user's properties.
  const effectiveUserId =
    targetUserId !== null ? targetUserId : user?.id ?? null;

  // Collect unique property names from certifications where the effective user
  // is a household member. No additional API requests — derived from Redux state.
  const propertyNames = useMemo<string[]>(() => {
    if (effectiveUserId === null) {
      return [];
    }
    const seen = new Set<string>();
    for (const cert of certifications) {
      const isMember = cert.cert_household?.cert_members?.some(
        (m) => m.user_id === effectiveUserId
      );
      if (!isMember) {
        continue;
      }
      const name = cert.property?.name;
      if (name && !seen.has(name)) {
        seen.add(name);
      }
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }, [certifications, effectiveUserId]);

  const backRoute = `/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}/${
    userId ?? ''
  }`;

  return (
    <Box sx={{ backgroundColor: hubPageBg, minHeight: '100%' }}>
      <PageHeader
        title="Property List"
        subtitle="Properties associated with this account"
        backRoute={backRoute}
      />

      <Stack gap={2} sx={{ p: 3 }}>
        <Box
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 2,
            boxShadow: '0px 2px 10px rgba(0,0,0,0.063)',
            overflow: 'hidden',
          }}
        >
          {propertyNames.length === 0 ? (
            <Stack
              alignItems="center"
              gap={1}
              sx={{ py: 6, px: 3, textAlign: 'center' }}
            >
              <HomeWorkIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
              <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
                No properties found for this account.
              </Typography>
            </Stack>
          ) : (
            propertyNames.map((name, i) => (
              <Box key={name}>
                <Stack sx={{ px: 2, py: 1.5 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                    {name}
                  </Typography>
                </Stack>
                {i < propertyNames.length - 1 && (
                  <Divider sx={{ borderColor: 'grey.300' }} />
                )}
              </Box>
            ))
          )}
        </Box>

        <Box
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 2,
            boxShadow: '0px 2px 10px rgba(0,0,0,0.063)',
            p: 2,
          }}
        >
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            If you see a property that you do not remember applying or
            certifying for using this account, please call{' '}
            <Link href="tel:18333922001" underline="always">
              1-833-392-2001
            </Link>{' '}
            or email{' '}
            <Link href="mailto:support@prontohousing.com" underline="always">
              support@prontohousing.com
            </Link>
            .
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

export default PropertyListPage;
